import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSyncScrollbarWidth } from "../../hooks/useSyncScrollbarWidth";
import {
  isDocumentToolPrepKey,
  isEditingToolPrepKey,
  isConversionToolPrepKey,
} from "../../../../utils/editorEntrySession";
import ToolPrepHowToUseModal from "./ToolPrepHowToUseModal";
import { resolveToolPrepHowToUse } from "../../../../config/tools/toolPrepHowToUseCatalog";
import { clearToolPrepSubjectPlacement } from "../../lib/toolPrepSubjectPlacement";
import "./ToolPrepPopup.css";

/** dashboard = Close returns to Dashboard; editor = dismiss overlay, keep canvas. */
export const ToolPrepCloseContext = createContext("dashboard");

/**
 * Reusable full-screen prep overlay on the Editor.
 * Left = tool content, Right = preview.
 * Close: document tools keep footer Close; others use a top-right X.
 * Optional Download stays. Done / Open in Editor continue.
 * Optional "How to use" link beside the title opens a help modal.
 */
const ToolPrepPopup = ({
  toolKey = "",
  title = "",
  left = null,
  preview = null,
  onDone = () => {},
  onCancel = () => {},
  busy = false,
  /** Label for the primary button while busy (when Open in Editor is hidden). */
  busyLabel = "Working…",
  doneLabel = "Done",
  cancelLabel = "Close",
  doneDisabled = false,
  /** When false, hides the primary Done / Generate footer button. */
  showDone = true,
  /** When a function, shows Download (saves file, stays in the popup). */
  onDownload = null,
  downloadLabel = "Download",
  downloadDisabled = false,
  /** Optional secondary footer action (e.g. Skip / Suggest crop). */
  onSecondaryAction = null,
  secondaryActionLabel = "Skip",
  secondaryActionDisabled = false,
  /** "button" (default) or "link" for text-style secondary action. */
  secondaryActionVariant = "button",
  /**
   * Footer button order. Keys: cancel | download | secondary | primary | openInEditor.
   * Default: cancel → download → secondary → primary → openInEditor
   * (or primary before secondary when footerPrimaryFirst is true).
   */
  footerActionOrder = null,
  /** When "stacked", primary is full-width with secondary below (often as link). */
  footerActionsLayout = "row",
  /** @deprecated Prefer footerActionOrder={['cancel','primary','secondary',...]} */
  footerPrimaryFirst = false,
  /** When a function, shows Open in Editor as the primary continue action. */
  onOpenInEditor = null,
  openInEditorLabel = "Open in Editor",
  openInEditorDisabled = false,
  headerLeft = null,
  /**
   * Optional help content. When set, shows a "How to use" link beside the title.
   * Shape: { aka?, summary?|bio?, steps?: string[], tip? }
   */
  howToUse = null,
  footerHint = null,
  footerHintClassName = "",
  /** @deprecated Left column uses a single scroll region; nested scroll is flattened via CSS. */
  bodyOwnsScroll = false,
  /** When true, right pane can flush to the popup edge (document tools with a loaded file). */
  hasSource = false,
  /** When true, Close asks before leaving if the user has uploaded or generated work. */
  confirmClose = false,
  /** Optional ref / callback ref for the full left column (header + body + footer). */
  leftColumnRef = null,
  /** Extra class on the left column (e.g. overlay open). */
  leftColumnClassName = "",
}) => {
  const leftBodyRef = useRef(null);
  const resolvedHowTo = useMemo(
    () => resolveToolPrepHowToUse(toolKey, howToUse),
    [toolKey, howToUse]
  );
  useSyncScrollbarWidth(leftBodyRef, [toolKey, left, resolvedHowTo]);

  const isDocumentTool = isDocumentToolPrepKey(toolKey);
  const showDownload = typeof onDownload === "function";
  const showSecondaryAction = typeof onSecondaryAction === "function";
  const showOpenInEditor = typeof onOpenInEditor === "function";
  const showPrimaryDone = showDone !== false;

  const resolvedFooterOrder = (() => {
    if (Array.isArray(footerActionOrder) && footerActionOrder.length) {
      return footerActionOrder;
    }
    if (footerPrimaryFirst) {
      return ["cancel", "download", "primary", "secondary", "openInEditor"];
    }
    return ["cancel", "download", "secondary", "primary", "openInEditor"];
  })();

  const visibleFooterKeys = {
    cancel: isDocumentTool && !busy,
    download: showDownload && !busy,
    secondary: showSecondaryAction && !busy,
    primary: showPrimaryDone,
    openInEditor: showOpenInEditor && !busy,
  };
  const actionCount = resolvedFooterOrder.filter(
    (key) => visibleFooterKeys[key]
  ).length;

  const closeReturnsTo = useContext(ToolPrepCloseContext);
  const closeDestinationLabel =
    closeReturnsTo === "editor" ? "the editor" : "the Dashboard";
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);

  useEffect(() => {
    if (!confirmClose) setConfirmingClose(false);
  }, [confirmClose]);

  useEffect(() => {
    if (!confirmingClose) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setConfirmingClose(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmingClose]);

  useEffect(() => {
    setHowToOpen(false);
    // Child previews publish first. Clearing here drops the untouched fit so
    // only a later drag/resize is kept for Open in Editor.
    clearToolPrepSubjectPlacement();
  }, [toolKey]);

  const handleCloseClick = () => {
    if (busy) return;
    if (confirmClose) {
      setConfirmingClose(true);
      return;
    }
    onCancel();
  };

  const handleDiscard = () => {
    setConfirmingClose(false);
    onCancel();
  };

  const footerButtons = {
    cancel: (
      <button
        key="cancel"
        type="button"
        className="tool-prep-popup__cancel-btn"
        onClick={handleCloseClick}
        disabled={busy}
      >
        {cancelLabel}
      </button>
    ),
    download: showDownload ? (
      <button
        key="download"
        type="button"
        className="tool-prep-popup__secondary-btn"
        onClick={onDownload}
        disabled={busy || downloadDisabled}
      >
        {downloadLabel}
      </button>
    ) : null,
    secondary: showSecondaryAction ? (
      secondaryActionVariant === "link" ? (
        <button
          key="secondary"
          type="button"
          className="tool-prep-popup__footer-link"
          onClick={onSecondaryAction}
          disabled={busy || secondaryActionDisabled}
        >
          {secondaryActionLabel}
        </button>
      ) : (
        <button
          key="secondary"
          type="button"
          className="tool-prep-popup__secondary-btn"
          onClick={onSecondaryAction}
          disabled={busy || secondaryActionDisabled}
        >
          {secondaryActionLabel}
        </button>
      )
    ) : null,
    primary: showPrimaryDone ? (
      <button
        key="primary"
        type="button"
        className={
          showOpenInEditor
            ? "tool-prep-popup__secondary-btn"
            : "tool-prep-popup__done-btn"
        }
        onClick={onDone}
        disabled={busy || doneDisabled}
      >
        {busy && !showOpenInEditor ? busyLabel : doneLabel}
      </button>
    ) : null,
    openInEditor: showOpenInEditor ? (
      <button
        key="openInEditor"
        type="button"
        className="tool-prep-popup__done-btn"
        onClick={onOpenInEditor}
        disabled={busy || openInEditorDisabled}
      >
        {openInEditorLabel}
      </button>
    ) : null,
  };

  const editingLayout =
    isEditingToolPrepKey(toolKey) || isConversionToolPrepKey(toolKey);

  return (
    <div
      className="tool-prep-popup"
      data-tool={toolKey || undefined}
      data-editing-layout={editingLayout ? "true" : undefined}
      data-has-source={hasSource ? "true" : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Tool setup"}
    >
      <div className="tool-prep-popup__panel">
        {!isDocumentTool ? (
          <button
            type="button"
            className="tool-prep-popup__close-x"
            onClick={handleCloseClick}
            disabled={busy}
            aria-label="Close"
          >
            <svg
              className="tool-prep-popup__close-x-icon"
              viewBox="0 0 14 14"
              width="14"
              height="14"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M1 1l12 12M13 1L1 13"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
        <div
          className={`tool-prep-popup__left${
            leftColumnClassName ? ` ${leftColumnClassName}` : ""
          }`}
          ref={leftColumnRef}
        >
          <div className="tool-prep-popup__left-header">
            {headerLeft}
            <div className="tool-prep-popup__title-row">
              {title ? (
                <h2 className="tool-prep-popup__title">{title}</h2>
              ) : null}
              {resolvedHowTo ? (
                <button
                  type="button"
                  className="tool-prep-popup__how-to-link"
                  onClick={() => setHowToOpen(true)}
                >
                  How to use
                </button>
              ) : null}
            </div>
          </div>
          <div ref={leftBodyRef} className="tool-prep-popup__left-body">
            {left}
          </div>
          <div className="tool-prep-popup__footer">
            {footerHint ? (
              <p
                className={`tool-prep-popup__footer-hint${
                  footerHintClassName ? ` ${footerHintClassName}` : ""
                }`}
              >
                {footerHint}
              </p>
            ) : null}
            <div
              className={`tool-prep-popup__footer-actions${
                footerActionsLayout === "stacked"
                  ? " tool-prep-popup__footer-actions--stacked"
                  : ""
              }`}
              data-count={String(actionCount)}
            >
              {resolvedFooterOrder.map((key) =>
                visibleFooterKeys[key] ? footerButtons[key] || null : null
              )}
            </div>
          </div>
        </div>
        <div className="tool-prep-popup__right">{preview}</div>
        {confirmingClose ? (
          <div
            className="tool-prep-popup__confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="tool-prep-discard-title"
            aria-describedby="tool-prep-discard-body"
          >
            <div className="tool-prep-popup__confirm-card">
              <h3
                id="tool-prep-discard-title"
                className="tool-prep-popup__confirm-title"
              >
                Discard this work?
              </h3>
              <p
                id="tool-prep-discard-body"
                className="tool-prep-popup__confirm-body"
              >
                Discard this result and return to {closeDestinationLabel}?
              </p>
              <div className="tool-prep-popup__confirm-actions">
                <button
                  type="button"
                  className="tool-prep-popup__secondary-btn"
                  onClick={() => setConfirmingClose(false)}
                >
                  Keep working
                </button>
                <button
                  type="button"
                  className="tool-prep-popup__done-btn"
                  onClick={handleDiscard}
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <ToolPrepHowToUseModal
        open={howToOpen}
        title="How to use"
        howToUse={resolvedHowTo}
        onClose={() => setHowToOpen(false)}
      />
    </div>
  );
};

export default ToolPrepPopup;
